import styled, { keyframes, css } from "styled-components";
import { ReactComponent as ChevronDownIcon } from "../../assets/icons/chevron-down.svg";

/* ─── Motion ─────────────────────────────────────────────────────────────── */

const panelFloat = keyframes`
  0%, 100% { filter: brightness(1); }
  50%      { filter: brightness(1.02); }
`;

/* ─── Modal shell — water-glass panel with outer aqua halo ──────────────── */

export const ModalContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 460px;
  max-height: 95dvh;
  padding: 0;
  border-radius: 28px;
  border: 1px solid var(--glass-edge);
  background:
    radial-gradient(
      120% 60% at 30% -10%,
      rgba(var(--glass-white-rgb), 0.96) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 55%,
      rgba(var(--glass-pale-rgb), 0.82) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.94) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 100%
    );
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  box-shadow:
    0 32px 64px -18px rgba(var(--color-brand-500-rgb), 0.4),
    0 12px 28px rgba(var(--color-brand-400-rgb), 0.22),
    inset 0 2px 0 rgba(255, 255, 255, 0.95),
    inset 0 -10px 22px rgba(var(--color-brand-200-rgb), 0.26),
    inset 0 0 0 1px rgba(255, 255, 255, 0.45);
  animation: ${panelFloat} 5.5s ease-in-out infinite;
  overflow: hidden;
  isolation: isolate;

  [data-theme="dark"] & {
    box-shadow:
      0 28px 56px -18px rgba(0, 0, 0, 0.6),
      0 10px 24px rgba(0, 0, 0, 0.4),
      inset 0 -10px 22px rgba(var(--color-brand-700-rgb), 0.18);
  }

  /* Top specular sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 32px;
    right: 32px;
    height: 20px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2px);
    pointer-events: none;
    z-index: 10;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  /* Bottom aqua underglow */
  &::after {
    content: "";
    position: absolute;
    bottom: -8px;
    left: 25%;
    right: 25%;
    height: 16px;
    background: radial-gradient(
      ellipse at center top,
      rgba(var(--color-brand-400-rgb), 0.42) 0%,
      rgba(var(--color-brand-400-rgb), 0) 70%
    );
    pointer-events: none;
    z-index: -1;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

/* ─── Header ─────────────────────────────────────────────────────────────── */

export const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 22px 24px 14px;
  gap: 14px;
  position: relative;
  z-index: 2;
`;

/* Left-side hero cluster: aqua drop icon + title/subtitle block */
export const HeaderHero = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex: 1;
  min-width: 0;
`;

/* Big 3D aqua water-drop badge to the left of the title */
export const HeroIcon = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1.5px solid rgba(var(--color-brand-400-rgb), 0.55);
  background: radial-gradient(
    circle at 30% 30%,
    rgba(var(--glass-pale-rgb), 0.95) 0%,
    rgba(170, 220, 255, 0.9) 50%,
    rgba(var(--color-brand-400-rgb), 0.85) 100%
  );
  box-shadow:
    0 8px 18px rgba(var(--color-brand-600-rgb), 0.3),
    inset 0 1.5px 0 rgba(255, 255, 255, 0.92),
    inset 0 -3px 8px rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: "";
    position: absolute;
    top: 4px;
    left: 10px;
    right: 10px;
    height: 7px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.88) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1.2px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  &::after {
    content: "";
    position: absolute;
    inset: -8px;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      rgba(var(--color-brand-400-rgb), 0.45) 0%,
      rgba(var(--color-brand-400-rgb), 0) 65%
    );
    filter: blur(3px);
    z-index: -1;
  }

  svg {
    position: relative;
    z-index: 1;
    width: 22px;
    height: 22px;
    color: var(--color-brand-700);
    path {
      stroke: var(--color-brand-700);
      fill: var(--color-brand-700);
    }
  }

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow:
      0 6px 14px rgba(0, 0, 0, 0.45),
      inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.16);
  }
`;

export const TitleGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

export const ModalTitle = styled.h3`
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: 22px;
  font-weight: 800;
  line-height: normal;
  letter-spacing: -0.03em;

  /* Brand gradient title */
  background: linear-gradient(
    0deg,
    var(--color-text-strong) 0%,
    var(--color-text-body) 45%,
    var(--color-brand-600) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 1px 2px rgba(var(--color-brand-600-rgb), 0.14));
`;

export const ModalSubtitle = styled.p`
  margin: 0;
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: -0.005em;
  line-height: normal;
`;

/* Frosted-glass circle close button */
export const CloseButton = styled.button`
  position: relative;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid var(--glass-edge-soft);
  background: radial-gradient(
    130% 100% at 30% 15%,
    rgba(var(--glass-white-rgb), 0.78) 0%,
    rgba(var(--glass-white-rgb), 0.5) 45%,
    rgba(var(--glass-pale-rgb), 0.38) 100%
  );
  backdrop-filter: blur(18px) saturate(180%);
  -webkit-backdrop-filter: blur(18px) saturate(180%);
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.22);
  cursor: pointer;
  outline: none;
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy);

  /* Top-left specular */
  &::before {
    content: "";
    position: absolute;
    top: 3px;
    left: 5px;
    width: 16px;
    height: 9px;
    border-radius: 50%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.85) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  svg {
    width: 16px;
    height: 16px;
    position: relative;
    z-index: 1;

    path {
      stroke: var(--color-brand-700);
    }
  }

  &:hover {
    transform: translateY(-1px) scale(1.04);
    border-color: rgba(var(--color-brand-400-rgb), 0.45);
  }

  [data-theme="dark"] & {
    border-color: transparent;
    background: var(--color-surface-3);
    box-shadow:
      0 4px 10px rgba(0, 0, 0, 0.45),
      inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.16);

    svg path {
      stroke: var(--color-brand-300);
    }
  }
`;

/* ─── Body ─────────────────────────────────────────────────────────────── */

export const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 10px 24px 18px;
  position: relative;
  z-index: 2;
  overflow-y: auto;
  flex: 1;

  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: rgba(var(--color-brand-300-rgb), 0.08);
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb {
    background: rgba(var(--color-brand-400-rgb), 0.38);
    border-radius: 999px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: rgba(var(--color-brand-400-rgb), 0.58);
  }
`;

export const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

export const SectionLabel = styled.span`
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  [data-theme="dark"] & {
    color: white;
  }
`;

/* ─── Segmented Control — frosted-glass pill row ─────────────────────── */

export const SegmentedControl = styled.div`
  position: relative;
  display: flex;
  width: 100%;
  padding: 4px;
  border-radius: 14px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.25);
  background: radial-gradient(
    120% 100% at 20% 0%,
    rgba(var(--glass-white-rgb), 0.7) 0%,
    rgba(var(--glass-pale-rgb), 0.52) 55%,
    rgba(var(--glass-pale-rgb), 0.44) 100%
  );
  backdrop-filter: blur(14px) saturate(160%);
  -webkit-backdrop-filter: blur(14px) saturate(160%);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.8),
    inset 0 -2px 5px rgba(var(--color-brand-200-rgb), 0.18);

  [data-theme="dark"] & {
    border-color: rgba(var(--color-brand-400-rgb), 0.18);
    background: rgba(var(--color-brand-900-rgb), 0.4);
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.4);
  }
`;

export const SegmentButton = styled.button`
  position: relative;
  flex: 1;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 9px 6px;
  border-radius: 10px;
  outline: none;
  cursor: ${(p) => (p.$disabled ? "default" : "pointer")};
  opacity: ${(p) => (p.$disabled ? 0.55 : 1)};
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: ${(p) => (p.$active ? 700 : 500)};
  letter-spacing: -0.005em;
  transition:
    background 220ms var(--ease-snappy),
    color 220ms var(--ease-snappy),
    border-color 220ms var(--ease-snappy),
    box-shadow 220ms var(--ease-snappy);

  ${(p) =>
    p.$active
      ? css`
          color: var(--glass-text-on-pill);
          border: 1px solid rgba(var(--glass-white-rgb), 0.72);
          background:
            radial-gradient(
              140% 100% at 20% 0%,
              rgba(255, 255, 255, 0.3) 0%,
              rgba(255, 255, 255, 0) 55%
            ),
            linear-gradient(
              180deg,
              var(--color-brand-400) 0%,
              var(--color-brand-600) 55%,
              var(--color-brand-700) 100%
            );
          box-shadow:
            0 8px 18px rgba(var(--color-brand-600-rgb), 0.32),
            inset 0 1px 0 rgba(255, 255, 255, 0.55),
            inset 0 -2px 5px rgba(var(--color-brand-700-rgb), 0.3);
          text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.28);

          [data-theme="dark"] & {
            border-color: transparent;
            border: none;
            box-shadow:
              0 6px 14px rgba(0, 0, 0, 0.5),
              inset 0 -2px 5px rgba(var(--color-brand-700-rgb), 0.3);
          }
        `
      : css`
          color: var(--color-brand-700);
          border: 1px solid transparent;
          background: transparent;

          &:hover:not(:disabled) {
            background: rgba(var(--color-brand-300-rgb), 0.2);
          }

          [data-theme="dark"] & {
            color: var(--color-brand-300);
            border-color: transparent;
            border: none;
          }
        `}
`;

export const Badge = styled.div`
  position: absolute;
  left: 50%;
  top: -7px;
  transform: translateX(-50%);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 13px;
  padding: 0 6px;
  border-radius: var(--radius-pill);
  border: 1px solid var(--glass-edge-soft);
  background: ${(p) =>
    p.$pro
      ? `radial-gradient(
           140% 100% at 20% 0%,
           rgba(255, 255, 255, 0.3) 0%,
           rgba(255, 255, 255, 0) 55%
         ),
         linear-gradient(180deg, var(--color-brand-400) 0%, var(--color-brand-600) 100%)`
      : `linear-gradient(180deg, #6ee7b7 0%, #10b981 100%)`};
  box-shadow:
    0 3px 8px
      ${(p) =>
        p.$pro
          ? "rgba(var(--color-brand-600-rgb), 0.3)"
          : "rgba(16, 185, 129, 0.35)"},
    inset 0 1px 0 rgba(255, 255, 255, 0.5);
  color: var(--glass-text-on-pill);
  font-family: var(--font-family-primary);
  font-size: 7px;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  text-shadow: 0 1px 1px rgba(0, 0, 0, 0.18);
  white-space: nowrap;
  pointer-events: none;
  z-index: 1;

  [data-theme="dark"] & {
    border-color: transparent;
    border: none;
  }
`;

/* Inline lock glyph that sits next to a locked button's main label. */
export const InlineLock = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 4px;
  flex-shrink: 0;
  pointer-events: none;
  opacity: 0.72;

  svg {
    height: 9px;
    width: auto;

    path {
      stroke: currentColor;
    }
  }
`;

/* ─── Seed Input ────────────────────────────────────────────────────────── */

export const SeedInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.3);
  background: radial-gradient(
    120% 60% at 30% -10%,
    rgba(var(--glass-white-rgb), 0.85) 0%,
    rgba(var(--glass-pale-rgb), 0.6) 60%,
    rgba(var(--glass-pale-rgb), 0.48) 100%
  );
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.85),
    inset 0 -2px 5px rgba(var(--color-brand-200-rgb), 0.18);
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.005em;
  outline: none;
  transition:
    border-color 220ms var(--ease-snappy),
    box-shadow 220ms var(--ease-snappy);

  &:focus {
    border-color: rgba(var(--color-brand-400-rgb), 0.55);
    box-shadow:
      0 0 0 3px rgba(var(--color-brand-400-rgb), 0.18),
      inset 0 1px 0 rgba(255, 255, 255, 0.95);
  }

  &::placeholder {
    color: var(--color-text-dim);
    font-weight: 500;
  }

  [data-theme="dark"] & {
    border-color: rgba(var(--color-brand-400-rgb), 0.18);
    background: rgba(var(--color-brand-900-rgb), 0.4);
    box-shadow: inset 0 1px 2px rgba(0, 0, 0, 0.4);

    &:focus {
      border-color: rgba(var(--color-brand-400-rgb), 0.55);
      box-shadow:
        0 0 0 3px rgba(var(--color-brand-400-rgb), 0.22),
        inset 0 1px 2px rgba(0, 0, 0, 0.4);
    }
  }
`;

/* ─── Settings Card — 2-column quality grid ───────────────────────────── */

export const SettingsCard = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 16px;
  border-radius: 16px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.22);
  background: radial-gradient(
    120% 60% at 30% -10%,
    rgba(var(--glass-white-rgb), 0.7) 0%,
    rgba(var(--glass-pale-rgb), 0.54) 60%,
    rgba(var(--glass-pale-rgb), 0.44) 100%
  );
  backdrop-filter: blur(14px) saturate(160%);
  -webkit-backdrop-filter: blur(14px) saturate(160%);
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.1),
    inset 0 1px 0 rgba(255, 255, 255, 0.85);
  overflow: hidden;

  [data-theme="dark"] & {
    border-color: rgba(var(--color-brand-400-rgb), 0.18);
    background: rgba(var(--color-brand-900-rgb), 0.32);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.45);
  }
`;

/* Resolution + Duration side-by-side on desktop, stacked on mobile */
export const QualityGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 16px;
  align-items: start;

  @media (max-width: 520px) {
    grid-template-columns: 1fr;
    gap: 16px;
  }
`;

export const SettingCol = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
`;

export const SettingLabel = styled.span`
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  [data-theme="dark"] & {
    color: white;
  }
`;

/* ─── Prompts — unified panel ──────────────────────────────────────────
   Single container that holds the clickable header AND the expandable
   content. When opened, header + textareas share the same water-glass
   background — they read as ONE unit, not separate stacked pieces. */

export const PromptsPanel = styled.div`
  display: flex;
  flex-direction: column;
  border-radius: 16px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.25);
  background: radial-gradient(
    120% 60% at 30% -10%,
    rgba(var(--glass-white-rgb), 0.7) 0%,
    rgba(var(--glass-pale-rgb), 0.54) 60%,
    rgba(var(--glass-pale-rgb), 0.44) 100%
  );
  backdrop-filter: blur(12px) saturate(160%);
  -webkit-backdrop-filter: blur(12px) saturate(160%);
  box-shadow:
    0 4px 10px rgba(var(--color-brand-500-rgb), 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.85);
  transition: border-color 220ms var(--ease-snappy);

  &:hover {
    border-color: rgba(var(--color-brand-400-rgb), 0.45);
  }

  [data-theme="dark"] & {
    border-color: rgba(var(--color-brand-400-rgb), 0.18);
    background: rgba(var(--color-brand-900-rgb), 0.32);
    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.45);
  }
`;

/* Clickable header at the top of the panel */
export const PromptsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  padding: 16px 18px;
  /* Thin aqua divider only when expanded */
  border-bottom: 1px solid
    ${(p) =>
      p.$expanded ? "rgba(var(--color-brand-300-rgb), 0.22)" : "transparent"};
  transition: border-color 260ms var(--ease-snappy);
`;

export const PromptsLabel = styled.span`
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 700;
  letter-spacing: -0.005em;
`;

export const PromptsChevron = styled(ChevronDownIcon)`
  width: 14px;
  height: 14px;
  transform: rotate(${(p) => (p.$expanded ? "180deg" : "0deg")});
  transition: transform 320ms var(--ease-snappy);

  path {
    stroke: var(--color-brand-700);
  }
`;

/* Expandable content — truly collapses to 0 when closed.
   max-height animation (not grid) so there's no leftover whitespace. */
export const PromptsContent = styled.div`
  max-height: ${(p) => (p.$expanded ? "700px" : "0")};
  padding: ${(p) => (p.$expanded ? "14px 18px 18px" : "0 18px")};
  opacity: ${(p) => (p.$expanded ? 1 : 0)};
  overflow-y: auto;

  display: flex;
  flex-direction: column;
  gap: 14px;
  transition:
    max-height 340ms var(--ease-snappy),
    padding 300ms var(--ease-snappy),
    opacity 220ms var(--ease-snappy);
`;

export const PromptGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  &:first-child {
    padding-top: 6px;
  }
`;

export const PromptLabel = styled.span`
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
`;

/* Prompt textareas — NO nested frosted chip. Transparent bg so they blend
   into PromptsPanel; just a subtle border + focus ring for definition. */
export const PromptTextarea = styled.textarea`
  width: 100%;
  min-height: ${(p) => (p.$minHeight ? "96px" : "60px")};
  padding: 12px 14px;
  border-radius: 12px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.28);
  background: var(--glass-base-3);
  color: var(--color-text-strong);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 500;
  line-height: normal;
  letter-spacing: -0.005em;
  resize: vertical;
  outline: none;
  transition:
    background 220ms var(--ease-snappy),
    border-color 220ms var(--ease-snappy),
    box-shadow 220ms var(--ease-snappy);

  &:hover {
    background: var(--glass-base-3);
  }

  &:focus {
    background: var(--glass-base-2);
    border-color: rgba(var(--color-brand-400-rgb), 0.55);
    box-shadow: 0 0 0 3px rgba(var(--color-brand-400-rgb), 0.16);
  }

  &::placeholder {
    color: var(--color-text-dim);
    font-weight: 500;
  }

  [data-theme="dark"] & {
    border-color: rgba(var(--color-brand-400-rgb), 0.18);
    background: rgba(0, 0, 0, 0.28);

    &:hover {
      background: rgba(0, 0, 0, 0.32);
    }

    &:focus {
      background: rgba(0, 0, 0, 0.36);
      border-color: rgba(var(--color-brand-400-rgb), 0.55);
      box-shadow: 0 0 0 3px rgba(var(--color-brand-400-rgb), 0.22);
    }
  }
`;

/* ─── Footer — time badge + one-line action row ─────────────────────────
   Redesigned: the summary bar is gone — estimated time collapses into a
   compact pill badge ABOVE the action row, and the cost moves INSIDE the
   Save button itself as a price chip. Cancel is now a subtle text link. */

export const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px 24px 22px;
  position: relative;
  z-index: 2;
  border-top: 1px solid rgba(var(--color-brand-300-rgb), 0.2);
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0) 0%,
    rgba(var(--glass-pale-rgb), 0.5) 100%
  );

  [data-theme="dark"] & {
    border-top-color: rgba(var(--color-brand-400-rgb), 0.14);
    background: linear-gradient(
      180deg,
      rgba(0, 0, 0, 0) 0%,
      rgba(0, 0, 0, 0.22) 100%
    );
  }
`;

/* Small pill showing estimated generation time — sits above the Save button */
export const TimeBadge = styled.span`
  align-self: center;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid rgba(var(--color-brand-300-rgb), 0.28);
  background: var(--glass-base-3);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.85),
    0 2px 6px rgba(var(--color-brand-500-rgb), 0.08);
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;

  [data-theme="dark"] & {
    border-color: rgba(var(--color-brand-400-rgb), 0.22);
    background: rgba(var(--color-brand-700-rgb), 0.18);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
    color: var(--color-brand-300);
  }
`;

export const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

/* Save button — clean brand-gradient pill with inline cost on the right.
   No more embedded dark chip — Save text and cost are just inline siblings
   with a subtle vertical divider between them, all in white. */
export const PrimaryButton = styled.button`
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  height: 48px;
  padding: 0 24px;
  border-radius: var(--radius-pill);
  border: 1px solid rgba(var(--glass-white-rgb), 0.72);
  background:
    radial-gradient(
      140% 100% at 20% 0%,
      rgba(255, 255, 255, 0.3) 0%,
      rgba(255, 255, 255, 0) 55%
    ),
    linear-gradient(
      180deg,
      var(--color-brand-400) 0%,
      var(--color-brand-600) 55%,
      var(--color-brand-700) 100%
    );
  box-shadow:
    0 14px 28px rgba(var(--color-brand-600-rgb), 0.34),
    0 3px 8px rgba(var(--color-brand-600-rgb), 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.6),
    inset 0 -3px 7px rgba(var(--color-brand-700-rgb), 0.3);
  color: var(--glass-text-on-pill);
  font-family: var(--font-family-primary);
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.005em;
  text-shadow: 0 1px 1px rgba(var(--color-brand-900-rgb), 0.28);
  cursor: pointer;
  outline: none;
  overflow: hidden;
  transition:
    transform 220ms var(--ease-snappy),
    filter 220ms var(--ease-snappy),
    box-shadow 220ms var(--ease-snappy);

  /* Top sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 16px;
    right: 16px;
    height: 10px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.75) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1.5px);
    pointer-events: none;
    z-index: 1;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    filter: brightness(1.06);
    box-shadow:
      0 18px 34px rgba(var(--color-brand-600-rgb), 0.44),
      inset 0 1px 0 rgba(255, 255, 255, 0.7),
      inset 0 -3px 8px rgba(var(--color-brand-700-rgb), 0.38);
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }

  [data-theme="dark"] & {
    border-color: transparent;
    border: none;
    box-shadow:
      0 10px 22px rgba(0, 0, 0, 0.55),
      inset 0 -3px 7px rgba(var(--color-brand-700-rgb), 0.3);

    &:hover:not(:disabled) {
      box-shadow:
        0 14px 30px rgba(0, 0, 0, 0.6),
        inset 0 -3px 8px rgba(var(--color-brand-700-rgb), 0.36);
    }
  }
`;

/* Inline cost group — drop + number, right of "Save" label.
   Thin vertical divider in front visually separates the two. */
export const PrimaryCostGroup = styled.span`
  position: relative;
  z-index: 2;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding-left: 14px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: -0.005em;
  font-variant-numeric: tabular-nums;

  /* Subtle vertical divider before the cost group */
  &::before {
    content: "";
    position: absolute;
    left: 0;
    top: 4px;
    bottom: 4px;
    width: 1px;
    border-radius: 999px;
    background: rgba(var(--glass-white-rgb), 0.32);
  }
`;

/* Cancel — subtle text link with underline on hover */
export const CancelLink = styled.button`
  flex-shrink: 0;
  padding: 0 14px;
  height: 50px;
  border: none;
  background: transparent;
  color: var(--color-brand-700);
  font-family: var(--font-family-primary);
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.005em;
  cursor: pointer;
  outline: none;
  transition:
    color 180ms var(--ease-snappy),
    text-decoration-color 180ms var(--ease-snappy);

  text-decoration: underline;
  text-decoration-color: transparent;
  text-underline-offset: 3px;

  &:hover {
    color: var(--color-brand-600);
    text-decoration-color: currentColor;
  }
`;
